import * as THREE from 'three';
import { resolveCharacter } from './character.js';
import { CollectibleField } from './collectibles.js';
import { spawnPopParticles } from './vfx.js';
import { attachTransparentVideo, readVideoHookParams } from './transparent-video.js';

const TARGET_URL = '/targets/challenge-target.zpt';

/**
 * Escena AR (Zappar Image Tracking) o modo prueba (cámara fija Three.js).
 * Docs: https://docs.zap.works/universal-ar/threejs/tracking/image-tracking/
 * Estado tracking: requiere .zpt entrenado — ver public/targets/README.md
 *
 * Hooks herramienta:
 * - characterUrl / ?character=/assets/guardian.glb
 * - ?video=…&videoMode=alpha|chroma (stub; sin asset = no-op)
 */
export class ChallengeScene {
  /**
   * @param {{
   *   canvas: HTMLCanvasElement,
   *   mode: 'ar' | 'test',
   *   characterUrl?: string | null,
   *   videoHook?: { url: string, mode?: 'alpha'|'chroma', chromaKey?: string } | null,
   *   onTracking?: (acquired: boolean) => void,
   *   onCollect?: (worldPos: THREE.Vector3, screen: {x:number,y:number}) => void,
   *   getScoreSnapshot?: () => { score: number, targetScore: number, combo: number } | null,
   * }} opts
   */
  constructor(opts) {
    this.canvas = opts.canvas;
    this.mode = opts.mode;
    this.characterUrl = opts.characterUrl || null;
    this.videoHook = opts.videoHook ?? readVideoHookParams();
    this.onTracking = opts.onTracking || (() => {});
    this.onCollect = opts.onCollect || (() => {});
    this.getScoreSnapshot = opts.getScoreSnapshot || (() => null);
    this.tracking = false;
    this.running = false;
    this.particles = [];
    this.videoPlane = null;
    this._raf = 0;
    this._clock = new THREE.Clock();
    this._targetMissing = false;
    this.ZapparThree = null;
  }

  async init() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true,
      preserveDrawingBuffer: true, // captura
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight, false);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.scene = new THREE.Scene();
    const hemi = new THREE.HemisphereLight(0xffffff, 0x223344, 1.1);
    const dir = new THREE.DirectionalLight(0xffffff, 0.85);
    dir.position.set(1, 2, 1);
    this.scene.add(hemi, dir);

    this.contentRoot = new THREE.Group();
    this.contentRoot.name = 'content-root';

    const { root: character, mixer, source } = await resolveCharacter(this.characterUrl);
    this.character = character;
    this.characterMixer = mixer;
    this.characterSource = source || 'procedural';
    this.contentRoot.add(character);
    this.characterMixer?.setScore?.(0, 50);

    if (this.videoHook?.url) {
      this.videoPlane = await attachTransparentVideo(this.contentRoot, this.videoHook);
      // Autoplay tras gesto de arranque de la experiencia (main ya hizo unlock audio).
      await this.videoPlane?.play?.();
    }

    // Ritmo para ~50 recolectadas en 20s: muchos orbes + respawn corto.
    this.collectibles = new CollectibleField(this.contentRoot, {
      count: 14,
      radius: 0.75,
      respawnMs: 320,
    });

    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();

    if (this.mode === 'ar') {
      await this._initAr();
    } else {
      this._initTest();
    }

    window.addEventListener('resize', this._onResize);
    this.canvas.addEventListener('pointerdown', this._onPointer);
    this.running = true;
    this._loop();
    return {
      targetMissing: this._targetMissing,
      mode: this.mode,
      characterSource: this.characterSource,
      videoAttached: Boolean(this.videoPlane),
    };
  }

  async _initAr() {
    const ZapparThree = await import('@zappar/zappar-threejs');
    this.ZapparThree = ZapparThree;
    ZapparThree.glContextSet(this.renderer.getContext());

    this.camera = new ZapparThree.Camera();
    this.scene.background = this.camera.backgroundTexture;

    const granted = await ZapparThree.permissionRequestUI();
    if (!granted) {
      ZapparThree.permissionDeniedUI();
      throw new Error('Permiso de cámara denegado');
    }
    this.camera.start();

    let imageTracker;
    try {
      // Vite dev hace fallback HTML (200) si falta el archivo — no basta con status.
      const probe = await fetch(TARGET_URL, { method: 'GET', cache: 'no-store' });
      const ct = probe.headers.get('content-type') || '';
      if (!probe.ok || ct.includes('text/html')) {
        throw new Error(`target ausente (HTTP ${probe.status}, type=${ct || 'unknown'})`);
      }
      // Consumir cuerpo para no dejar el fetch a medias; el loader volverá a pedir la URL.
      await probe.arrayBuffer();
      imageTracker = new ZapparThree.ImageTrackerLoader().load(TARGET_URL);
    } catch (err) {
      this._targetMissing = true;
      console.warn('[ar] .zpt ausente o no cargable — tracking no disponible:', err);
      // Escena AR sin target: cámara activa pero sin ancla. Contenido no visible hasta .zpt.
      this.scene.add(this.contentRoot);
      this.contentRoot.visible = false;
      return;
    }

    this.imageTracker = imageTracker;
    this.trackerGroup = new ZapparThree.ImageAnchorGroup(this.camera, imageTracker);
    this.trackerGroup.add(this.contentRoot);
    this.scene.add(this.trackerGroup);

    imageTracker.onVisible.bind(() => {
      this.tracking = true;
      this.contentRoot.visible = true;
      this.onTracking(true);
    });
    imageTracker.onNotVisible.bind(() => {
      this.tracking = false;
      this.onTracking(false);
    });
  }

  _initTest() {
    this.camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 100);
    this.camera.position.set(0, 1.2, 2.4);
    this.camera.lookAt(0, 0.4, 0);
    this.scene.background = new THREE.Color(0x0b1f2a);

    // Plano simulado del target (solo modo prueba — NO es Image Tracking)
    const plane = new THREE.Mesh(
      new THREE.PlaneGeometry(1.6, 1),
      new THREE.MeshStandardMaterial({ color: 0x1a3a4a, roughness: 0.9 }),
    );
    plane.rotation.x = -Math.PI / 2;
    plane.position.y = 0;
    this.scene.add(plane);

    const grid = new THREE.GridHelper(3, 12, 0x2ec4b6, 0x163642);
    grid.position.y = 0.001;
    this.scene.add(grid);

    this.contentRoot.position.set(0, 0, 0);
    this.scene.add(this.contentRoot);
    this.tracking = true;
    this.onTracking(true);
  }

  _onResize = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h, false);
    if (this.mode === 'test' && this.camera?.isPerspectiveCamera) {
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
    }
  };

  _onPointer = (event) => {
    if (!this.running) return;
    if (this.mode === 'ar' && !this.tracking) return;

    const rect = this.canvas.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hits = this.raycaster.intersectObjects(this.collectibles.items, false);
    if (!hits.length) return;

    const collected = this.collectibles.tryCollect(hits[0].object);
    if (!collected) return;

    const world = hits[0].point.clone();
    const pop = spawnPopParticles(THREE, this.contentRoot, collected.position.clone());
    this.particles.push(pop);
    const counted = this.onCollect(world, { x: event.clientX, y: event.clientY });
    if (counted === false) return;
    const snap = this.getScoreSnapshot?.();
    if (snap) {
      this.characterMixer?.celebrate?.(snap.combo || 1);
      this.characterMixer?.setScore?.(snap.score, snap.targetScore);
    } else {
      this.characterMixer?.celebrate?.(1);
    }
  };

  _loop = () => {
    if (!this.running) return;
    this._raf = requestAnimationFrame(this._loop);
    const dt = Math.min(0.05, this._clock.getDelta());

    if (this.mode === 'ar' && this.camera?.updateFrame) {
      this.camera.updateFrame(this.renderer);
    }

    this.characterMixer?.update(dt);
    this.videoPlane?.update?.();
    this.collectibles?.update(dt);
    this.particles = this.particles.filter((p) => p.update(dt));

    this.renderer.render(this.scene, this.camera);
  };

  /** Pausa lógica visual (tracking lost). */
  setContentActive(active) {
    if (this.mode === 'test') return;
    this.contentRoot.visible = active && this.tracking;
  }

  getCanvas() {
    return this.canvas;
  }

  dispose() {
    this.running = false;
    cancelAnimationFrame(this._raf);
    window.removeEventListener('resize', this._onResize);
    this.canvas.removeEventListener('pointerdown', this._onPointer);
    this.videoPlane?.dispose?.();
    this.videoPlane = null;
    this.collectibles?.dispose();
    this.renderer?.dispose();
    if (this.mode === 'ar' && this.camera?.stop) {
      try {
        this.camera.stop();
      } catch {
        /* ignore */
      }
    }
  }
}

export function targetFilePending() {
  return TARGET_URL;
}
